import React, { useState, useEffect } from "react";
import CookingFeedbackDialog from "./CookingFeedbackDialog";
import { forkRecipeApi } from "../../services/recipeService";

// Subcomponents
import RecipeHeader from "./RecipeView/RecipeHeader";
import RecipeTopNav from "./RecipeView/RecipeTopNav";
import IngredientsSidebar from "./RecipeView/IngredientsSidebar";
import StepsCarousel from "./RecipeView/StepsCarousel/StepsCarousel";

const RecipeView = ({ recipe, onBack, onEdit, onFork, logFeedback, getFeedback, onPlan, initialServings, showShoppingListAction = false }) => {
    const [checkedIngredients, setCheckedIngredients] = useState(new Set());
    const [servings, setServings] = useState(initialServings || recipe?.servings || 2);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showIngredients, setShowIngredients] = useState(true);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (getFeedback && recipe?.id) {
            getFeedback(recipe.id);
        }
    }, [getFeedback, recipe?.id]);

    if (!recipe) return null;

    const handleLogFeedback = async (data) => {
        if (logFeedback && recipe?.id) {
            await logFeedback(recipe.id, data);
        }
        setShowFeedback(false);
        if (onBack) onBack();
    };

    const toggleIngredient = (id) => {
        const newSet = new Set(checkedIngredients);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setCheckedIngredients(newSet);
    };

    const handleServingsChange = (delta) => {
        setServings(prev => Math.max(1, prev + delta));
    };

    const handleFork = async () => {
        if (!window.confirm("Möchtest du eine Kopie dieses Rezepts erstellen und bearbeiten?")) return;

        try {
            const forkedRecipe = await forkRecipeApi({ id: recipe.id });
            if (onFork) {
                onFork(forkedRecipe);
            }
        } catch (error) {
            console.error("Fork failed:", error);
            alert("Fehler beim Erstellen der Kopie.");
        }
    };

    const sortedSteps = recipe.steps ? [...recipe.steps].sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0)) : [];

    return (
        <div className={`container-fluid animate-fade-in bg-white d-flex flex-row position-relative p-0`}>

            <IngredientsSidebar
                recipe={recipe}
                servings={servings}
                onServingsChange={handleServingsChange}
                checkedIngredients={checkedIngredients}
                onToggleIngredient={toggleIngredient}
                isOpen={showIngredients}
                onClose={() => setShowIngredients(false)}
                isDesktop={isDesktop}
            />

            <div className="d-flex flex-column flex-grow-1 min-vh-100 overflow-x-hidden">
                <RecipeTopNav
                    onBack={onBack}
                    onForkAction={handleFork}
                    onPlan={onPlan}
                    onEdit={onEdit}
                    recipe={recipe}
                />

                <div className={`d-flex flex-column flex-grow-1 px-4`}>
                    <RecipeHeader
                        recipe={recipe}
                        servings={servings}
                    />

                    <StepsCarousel
                        steps={sortedSteps}
                        recipe={recipe}
                        servings={servings}
                        showIngredients={showIngredients}
                        setShowIngredients={setShowIngredients}
                        onShowFeedback={() => setShowFeedback(true)}
                    />
                </div>

                <CookingFeedbackDialog
                    show={showFeedback}
                    onHide={() => setShowFeedback(false)}
                    onSubmit={handleLogFeedback}
                    recipeTitle={recipe.title}
                />
            </div>
        </div>
    );
};

export default RecipeView;
